import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';

/**
 * A structured, always-dismissible modal panel for surfacing contextual messages,
 * tips, promotions, or system updates.
 *
 * The panel exposes a single default content slot that accepts one or more elements
 * and widgets. Consumers are responsible for all interior layout using Tailwind or
 * standard CSS. A dismiss button is always rendered in the top-right corner.
 * The component is presentational only — it emits `asc-dismiss` on user dismissal
 * but never removes itself from the DOM. The consumer owns all visibility management.
 *
 * ```html
 * <asc-modal label="Account update">
 *   <div class="p-3xl flex flex-col gap-xl">
 *     <div class="flex flex-col gap-xxs">
 *       <span style="font-size:var(--asc-font-size-text-md);font-weight:var(--asc-font-weight-semibold);color:var(--asc-text-primary);">New account created</span>
 *       <span style="font-size:var(--asc-font-size-text-sm);color:var(--asc-text-tertiary);">Your account details will be shown once confirmed.</span>
 *     </div>
 *     <div class="flex gap-lg">
 *       <button style="flex:1;">Confirm</button>
 *       <button style="flex:1;">Cancel</button>
 *     </div>
 *   </div>
 * </asc-modal>
 * ```
 *
 * @element asc-modal
 *
 * @fires asc-dismiss - Fired when the user activates the dismiss button. Detail: `{}`
 *
 * @slot - The primary content area. Accepts one or more elements and widgets.
 *         Hidden when empty. Consumers are responsible for interior layout.
 *
 * @csspart base    - Root `<section>`; border, background, shadow, overall layout.
 * @csspart content - `<div>` wrapping the default slot; hidden when empty.
 * @csspart dismiss - The dismiss `<button>`; always rendered, absolutely positioned top-right.
 *
 * @cssprop --asc-modal-bg                  - Background color. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-modal-border-color        - Border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-modal-border-radius       - Corner radius. Default: `var(--asc-radius-lg)`
 * @cssprop --asc-modal-border-width        - Border thickness. Default: `var(--asc-border-width)`
 * @cssprop --asc-modal-dismiss-color       - Dismiss button icon color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-modal-dismiss-color-hover - Dismiss button icon hover color. Default: `var(--asc-fg-primary)`
 * @cssprop --asc-modal-shadow              - Box shadow. Default: composed from `--asc-shadow-xl_*` tokens
 * @cssprop --asc-modal-focus-ring-color    - Focus ring color for the dismiss button. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-modal-max-width-sm        - Max-width for the `sm` size. Applied at all breakpoints. Default: `400px`
 * @cssprop --asc-modal-max-width-md        - Max-width for the `md` size. Applied at ≥768 px. Default: `768px`
 * @cssprop --asc-modal-max-width-lg        - Max-width for the `lg` size. Applied at ≥1024 px. Default: `1024px`
 * @cssprop --asc-modal-viewport-gutter     - Per-side viewport gutter used in the `min()` overflow guard. Default: `var(--asc-spacing-3xl)` (24 px)
 */
@customElement('asc-modal')
export class AscModal extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      max-width: none;
    }

    /* ─── Size variants ─────────────────────────────────────── */
    /*
     * sm applies at all breakpoints — 400px is comfortably within mobile widths.
     * md and lg only activate above their respective breakpoints; below them the
     * modal naturally fills its parent (mobile-first, no max-width override).
     * xl is fully fluid — the consumer's container governs the width.
     *
     * The min() guard ensures the modal never bleeds past the viewport edge by
     * reserving --asc-modal-viewport-gutter (default 24px) on each side.
     */

    :host([size='sm']) {
      max-width: min(
        var(--asc-modal-max-width-sm, 400px),
        calc(100vw - 2 * var(--asc-modal-viewport-gutter, var(--asc-spacing-3xl)))
      );
    }

    @media (min-width: 768px) {
      :host([size='md']) {
        max-width: min(
          var(--asc-modal-max-width-md, 768px),
          calc(100vw - 2 * var(--asc-modal-viewport-gutter, var(--asc-spacing-3xl)))
        );
      }
    }

    @media (min-width: 1024px) {
      :host([size='lg']) {
        max-width: min(
          var(--asc-modal-max-width-lg, 1024px),
          calc(100vw - 2 * var(--asc-modal-viewport-gutter, var(--asc-spacing-3xl)))
        );
      }
    }

    :host([size='xl']) {
      max-width: none;
    }

    /* ─── Base ───────────────────────────────────────────────── */
    [part='base'] {
      position: relative;
      overflow: hidden;
      background-color: var(--asc-modal-bg, var(--asc-bg-primary));
      border: var(--asc-modal-border-width, var(--asc-border-width)) solid
        var(--asc-modal-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-modal-border-radius, var(--asc-radius-lg));
      box-shadow: var(
        --asc-modal-shadow,
        0px 20px 24px -4px var(--asc-shadow-xl_01),
        0px 8px 8px -4px var(--asc-shadow-xl_02),
        0px 3px 3px -1.5px var(--asc-shadow-xl_03)
      );
    }

    /* ─── Dismiss button ─────────────────────────────────────── */
    [part='dismiss'] {
      position: absolute;
      top: var(--asc-spacing-lg);
      right: var(--asc-spacing-lg);
      /* z-index: 1 ensures the button is explicitly above the slotted content
         in the same stacking context. */
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px; /* no-token: WCAG 2.5.5 minimum touch target */
      height: 44px; /* no-token: see above */
      padding: var(--asc-spacing-md);
      border: none;
      border-radius: var(--asc-radius-md);
      background: transparent;
      color: var(--asc-modal-dismiss-color, var(--asc-fg-tertiary));
      cursor: pointer;
      pointer-events: auto;
      box-sizing: border-box;
      transition: color var(--asc-transition-fast),
        background-color var(--asc-transition-fast);
    }

    [part='dismiss']:hover {
      color: var(--asc-modal-dismiss-color-hover, var(--asc-fg-primary));
      background-color: var(--asc-bg-primary_hover);
    }

    [part='dismiss']:focus-visible {
      outline: 2px solid var(--asc-modal-focus-ring-color, var(--asc-focus-ring));
      outline-offset: 2px;
    }

    [part='dismiss'] svg {
      display: block;
      width: 24px; /* no-token: standard icon size within dismiss button */
      height: 24px; /* no-token: see above */
      flex-shrink: 0;
    }

    /* ─── Content ────────────────────────────────────────────── */
    [part='content'] {
      display: block;
    }

    [part='content'][hidden] {
      display: none;
    }
  `;

  /**
   * Controls the max-width of the modal above its respective breakpoint.
   * Defaults to `xl` to preserve the historical fluid-width behavior.
   * - `sm` — 400 px, applies at all sizes (safe for mobile).
   * - `md` — 768 px, activates at ≥ 768 px (tablet).
   * - `lg` — 1024 px, activates at ≥ 1024 px (desktop).
   * - `xl` — fluid; no max-width constraint.
   *
   * Below its breakpoint a size variant has no max-width, so the modal fills
   * its parent — preserving existing mobile behaviour for all sizes.
   */
  @property({ type: String, reflect: true })
  size: 'sm' | 'md' | 'lg' | 'xl' = 'xl';

  /** Accessible label for the dismiss button. Override to provide modal-specific context (e.g., `'Dismiss promotional offer'`). */
  @property({ type: String, attribute: 'dismiss-label' })
  dismissLabel = 'Dismiss modal';

  /** Accessible label for the inner `<section>` landmark region. Consumers should set a descriptive value (e.g., `'System update'`). */
  @property({ type: String })
  label = 'Modal';

  @state() private _hasContent = false;

  private _handleSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasContent = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private readonly _handleDismiss = (): void => {
    dispatch(this, 'asc-dismiss', {});
  };

  override render() {
    return html`
      <section part="base" aria-label="${this.label}">
        <button
          part="dismiss"
          type="button"
          aria-label="${this.dismissLabel}"
          @click=${this._handleDismiss}
        >
          <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <div part="content" ?hidden=${!this._hasContent}>
          <slot @slotchange=${this._handleSlotChange}></slot>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-modal': AscModal;
  }
}
